import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import appConfig from "../../../appConfig";
import { useConfig } from "../../../context/ConfigContext";
import { useGeolocation } from "../../../hooks/useGeolocation";
import { useGoogleMaps } from "../../../hooks/useGoogleMaps";
import { ProcessedRestaurant, useProcessedRestaurants } from "../../../hooks/useProcessedRestaurants";
import { useRestaurantMap } from "../../../hooks/useRestaurantMap";
import { Restaurant } from "../../../interfaces/Catalogue";
import { updateAppState } from "../../../store/app/appActions";
import { RootState } from "../../../store/rootState";
import { openExternalDirections } from "../../../utils/mapUtils";
import MapView from "./components/MapView/MapView";
import ModalHeader from "./components/ModalHeader/ModalHeader";
import RestaurantList from "./components/RestaurantList/RestaurantList";
import SearchBar from "./components/SearchBar/SearchBar";
import Toast from "./components/Toast/Toast";
import styles from "./RestaurantSelectionModal.module.scss";

interface Props {
  visible: boolean;
  onClose: () => void;
  onSelect: (restaurant: Restaurant) => void;
}

const RestaurantSelectionModal: React.FC<Props> = ({ visible, onClose, onSelect }) => {
  const { config } = useConfig();
  const dispatch = useDispatch();
  const primaryColor = config?.primaryColor || "#6366F1";
  const logoUrl = config?.appLogoImage || "";

  const [search, setSearch] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const itemRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const restaurants = useSelector((s: RootState) => s.shop.shops);
  const selectedRestaurant = useSelector((s: RootState) => s.shop.selectedRestaurant);

  const { userLocation, status: locationStatus, request: requestGeo } = useGeolocation();
  const mapsReady = useGoogleMaps(visible);
  const processed = useProcessedRestaurants(restaurants, search, userLocation);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  const handleSelect = (r: Restaurant) => {
    dispatch(updateAppState({ favoriteRestaurantId: r.id }));
    onSelect(r);
  };

  const focusItem = (id: number) => {
    itemRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const {
    mapRef, drawRoute, clearRoute, recenter, focusRestaurant, routeActive, routeInfo,
  } = useRestaurantMap({
    mapsReady, visible, restaurants,
    selectedId: selectedRestaurant?.id,
    userLocation, primaryColor, logoUrl,
    onSelect: handleSelect,
    onItemFocus: focusItem,
  });

  // Track which restaurant the active route points to (for the external fallback link)
  const [routeTarget, setRouteTarget] = useState<Restaurant | null>(null);

  // Auto-request location on open
  useEffect(() => {
    if (visible) requestGeo();
  }, [visible, requestGeo]);

  // Body scroll lock
  useEffect(() => {
    if (visible) {
      const scrollY = window.scrollY;
      document.body.style.cssText = `overflow:hidden;position:fixed;top:-${scrollY}px;width:100%`;
    } else {
      const top = document.body.style.top;
      document.body.style.cssText = "";
      window.scrollTo(0, parseInt(top || "0") * -1);
    }
    return () => { document.body.style.cssText = ""; };
  }, [visible]);

  const requestLocation = () => {
    if (locationStatus === "loading") return;
    if (locationStatus === "granted") { recenter(); return; }
    requestGeo(() =>
      showToast("Accès à la position refusé. Activez-la dans les paramètres du navigateur.")
    );
  };

  const handleItemClick = (item: ProcessedRestaurant) => {
    handleSelect(item);
    focusRestaurant(item.id);
  };

  // In-app directions: draw route on map (falls back to external if no location/route)
  const handleDirections = async (item: ProcessedRestaurant) => {
    if (!userLocation) {
      openExternalDirections(item, userLocation);
      return;
    }
    setRouteTarget(item);
    const ok = await drawRoute(item);
    if (!ok) {
      openExternalDirections(item, userLocation);
      showToast("Itinéraire indisponible — ouverture dans Google Maps.");
    }
  };

  if (!visible) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {/* <div className={styles.handle} /> */}

        <ModalHeader count={processed.length} hasLocation={!!userLocation} />

        <SearchBar
          value={search}
          onChange={setSearch}
          locationStatus={locationStatus}
          onRequestLocation={requestLocation}
        />

        {toast && <Toast message={toast} />}

        {appConfig.GOOGLE_MAPS_API_KEY && (
          <MapView
            mapRef={mapRef}
            mapsReady={mapsReady}
            showRecenter={locationStatus === "granted"}
            onRecenter={recenter}
            routeActive={routeActive}
            routeInfo={routeInfo}
            onCloseRoute={clearRoute}
            onOpenExternal={() => routeTarget && openExternalDirections(routeTarget, userLocation)}
          />
        )}

        <RestaurantList
          items={processed}
          selectedId={selectedRestaurant?.id}
          primaryColor={primaryColor}
          itemRefs={itemRefs}
          onItemClick={handleItemClick}
          onDirections={handleDirections}
        />
      </div>
    </div>
  );
};

export default RestaurantSelectionModal;